import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import { useContext, useEffect, useState } from "react";
import {
  savePushSubscription,
  deletePushSubscription,
  sendTestPushNotification,
} from "../apollo/server";
import UserContext from "../context/User";
import ConfigurationContext from "../context/Configuration";

const SAVE_PUSH_SUBSCRIPTION = gql`
  ${savePushSubscription}
`;
const DELETE_PUSH_SUBSCRIPTION = gql`
  ${deletePushSubscription}
`;
const SEND_TEST_PUSH = gql`
  ${sendTestPushNotification}
`;

// pushManager.subscribe wants the VAPID public key as a raw Uint8Array, not
// the base64url string the server/Configuration hands us.
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

// Shared by the manual "Enable Notifications" button (Settings) and the
// automatic post-install prompt — one subscribe/unsubscribe/test
// implementation so the two never drift apart.
export default function usePushNotifications() {
  const { profile } = useContext(UserContext);
  const configuration = useContext(ConfigurationContext);

  const supported = "serviceWorker" in navigator && "PushManager" in window;

  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "denied"
  );
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});

  const [savePushSub] = useMutation(SAVE_PUSH_SUBSCRIPTION);
  const [deletePushSub] = useMutation(DELETE_PUSH_SUBSCRIPTION);
  const [sendTestPush] = useMutation(SEND_TEST_PUSH);

  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        setSubscribed(!!existing);
      } catch (e) {
        // Not registered yet — fine, enable() below will register it.
      }
    })();
  }, [supported]);

  const enable = async () => {
    if (!profile?._id) {
      setError({ type: "error", message: "Please log in to enable notifications." });
      return false;
    }
    if (!configuration?.vapidKey) {
      setError({ type: "error", message: "Push isn't configured yet — try again later." });
      return false;
    }
    setLoading(true);
    try {
      // requestPermission() first, before any other await — iOS Safari only
      // shows the permission dialog when the call is directly inside a user
      // gesture (tap/click); once we've awaited something else first, it
      // silently no-ops instead of prompting.
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") return false;

      const registration = await navigator.serviceWorker.ready;

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(configuration.vapidKey),
        });
      }
      const json = subscription.toJSON();
      await savePushSub({
        variables: { endpoint: json.endpoint, p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      setSubscribed(true);
      setError({ type: "success", message: "Notifications enabled." });
      return true;
    } catch (e) {
      setError({ type: "error", message: e.message || "Couldn't enable notifications." });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const disable = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await deletePushSub({ variables: { endpoint: subscription.endpoint } });
        await subscription.unsubscribe();
      }
      setSubscribed(false);
      setError({ type: "success", message: "Notifications turned off." });
    } catch (e) {
      setError({ type: "error", message: e.message || "Couldn't disable notifications." });
    } finally {
      setLoading(false);
    }
  };

  const sendTest = async () => {
    try {
      await sendTestPush();
      setError({ type: "success", message: "Test notification sent — look for it in a few seconds." });
    } catch (e) {
      setError({ type: "error", message: e.message || "Couldn't send test notification." });
    }
  };

  return {
    supported,
    permission,
    subscribed,
    loading,
    error,
    clearError: () => setError({}),
    enable,
    disable,
    sendTest,
  };
}
